import { isMe, type ViewerIdentity } from "../../session-scope.js";

// Pure default-daemon selection — extracted from DaemonState.loadedId so the
// ownership/access priority is unit-testable without the runes ($state) class.
//
// NEVER fall back to a daemon the user doesn't own / can't access: silently
// landing on someone else's daemon is how cybos got created on a stranger's
// machine. Priority:
//   1. own + online        (yours and reachable — the happy path)
//   2. accessible + online  (reachable, you hold a grant)
//   3. own (even offline)   (still YOURS — surface it so the user brings it
//                            online, instead of silently using another's)
//   4. accessible (offline) (a daemon you genuinely have access to)
//   5. null                 (nothing usable — better than a foreign daemon)
// Online daemons the user may run code on (owner OR daemon_access grant).
// Capability/provider surfaces must use THIS, not the raw online list:
// offering "Set up →" on a foreign daemon advertises other members' machines
// as setup targets the user can't actually use. The same access matrix that
// gates spawns and slash-daemon designation (slash-daemon-access.ts server
// side) applies to the OFFER, not just the execution.
export function accessibleOnlineDaemons<D extends { id: string; ownerId: string }>(
  daemons: readonly D[],
  isOnline: (daemon: D) => boolean,
  hasAccess: (daemon: D) => boolean,
): D[] {
  return daemons.filter((d) => isOnline(d) && hasAccess(d));
}

// `viewer` is the viewer IDENTITY (a bare id still type-checks, and still degrades to
// the old compare). `daemons.owner_id` is stamped with the daemon-LOCAL id when the
// daemon claims itself over its own socket (workspace-relay syncDaemonToPg) and with
// the cloud id on a relay claim, so `ownerId === currentUserId` skipped the viewer's
// OWN machine at priorities 1 and 3 and dropped them onto a teammate's at 2.
export function pickDefaultDaemon<D extends { id: string; ownerId: string }>(
  daemons: readonly D[],
  viewer: ViewerIdentity,
  isOnline: (daemon: D) => boolean,
  hasAccess: (daemon: D) => boolean,
): string | null {
  if (daemons.length === 0) return null;
  const ownedOnline = daemons.find((d) => isMe(d.ownerId, viewer) && isOnline(d));
  if (ownedOnline) return ownedOnline.id;
  const accessibleOnline = daemons.find((d) => isOnline(d) && hasAccess(d));
  if (accessibleOnline) return accessibleOnline.id;
  const owned = daemons.find((d) => isMe(d.ownerId, viewer));
  if (owned) return owned.id;
  const accessible = daemons.find((d) => hasAccess(d));
  if (accessible) return accessible.id;
  return null;
}

// Which daemon a cybo's session must SPAWN on. The cybo's OWN home machine wins
// over whatever the viewer happens to be looking at (CYBORG-680 layer 1): every
// caller used to home only LOCAL (disk) cybos and send a workspace cybo to the
// VIEWER's daemon, so a member whose machine isn't the cybo's home launched it
// on a box that holds neither the cybo's credentials nor its files.
//   • LOCAL (disk) cybo → `daemonId` — the only machine that has it on disk.
//   • workspace cybo    → `homeDaemonId`, the explicit home chosen at creation
//     and carried across daemons (`daemonId` is disk PROVENANCE only, per
//     types.ts, so it is deliberately not consulted for these).
//   • neither           → the caller's fallback, which must be the viewer's
//     EFFECTIVE daemon (online-first), never a raw sticky selection: an explicit
//     sidebar pick survives the machine going offline for days (layer 2).
export function resolveCyboSpawnDaemonId(
  cybo: { id: string; isLocal?: boolean; daemonId?: string | null; homeDaemonId?: string | null },
  fallbackDaemonId: string | null | undefined,
): string | undefined {
  const isLocalCybo = cybo.isLocal || cybo.id.startsWith("local:");
  const home = isLocalCybo ? cybo.daemonId : cybo.homeDaemonId;
  return home ?? fallbackDaemonId ?? undefined;
}

// The viewer's OWN daemon, for MACHINE-IDENTITY comparisons (the Remote/Local
// session badge, session-scope.ts's `isRemoteSession`) — deliberately NARROWER
// than pickDefaultDaemon/effectiveId above, which pick "which daemon should a
// NEW action target" and legitimately prefer a granted-ONLINE teammate daemon
// over the viewer's own offline one. Feeding THAT into isRemoteSession inverts
// the badge: a viewer whose own daemon is offline but holds a grant elsewhere
// would have their OWN (offline) session compare unequal to the granted
// daemon and read "Remote", while the teammate's (now "effective") daemon
// reads "Local" (the Seb/Rick bug, inverted).
//
// Grants NEVER enter this resolution — only daemons the viewer OWNS. But a
// NAIVE "is this session's daemon owned by me" set-membership check is its
// own bug for a viewer who owns MORE THAN ONE daemon (e.g. a Mac AND a PC):
// it would call a session on their OTHER own machine "Local" too, when it's
// genuinely a different box in front of them right now. isRemoteSession
// compares against ONE specific viewer-daemon id (matching "am I at THIS
// exact machine" — session-scope.ts's own definition of what "Remote" MUST
// mean), so the fix is choosing WHICH owned daemon that is — not switching
// the comparison to "owned by me at all".
//
// Priority (owned daemons ONLY):
//   1. the explicitly selected daemon, if the viewer owns it (an explicit
//      pick is the strongest signal of "the machine I'm at")
//   2. an owned daemon that's online (first match — the same "arbitrary
//      among several simultaneously-online owned machines" limitation
//      pickDefaultDaemon already carries; there's no stronger signal available)
//   3. an owned daemon, any status (still surfaces "mine" rather than reading
//      unknown when the viewer's only daemon happens to be offline)
//   4. null (owns nothing here — isRemoteSession then stays conservative and
//      renders no badge at all, never a guess)
//
// CONFIRMED GAP (round-3 review): priority-1 (`selectedId`) is UNREACHABLE
// from the agent-DM/DMs-sessions routes — `daemonState.selectedId` is only
// ever set by the machine surfaces (Settings › Machines, DaemonDetail), which
// neither route is, and the workspace layout nulls it on mount.
// So those two callers always fall through to priority-2/3 — "first
// owned+online (or owned) daemon IN LIST ORDER". That's now DETERMINISTIC
// (pg-sync.ts `getDaemonsForWorkspace` has an `ORDER BY`), so it's an
// acceptable, stable answer on its own — but it's still not necessarily
// "which machine you're at" for anyone who owns 2+ such daemons. Prefer
// `resolveViewerDaemonId` below at any Remote/Local-badge call site: it tries
// the one real, non-inferred signal first (the connected daemon's own id,
// where reachable) before falling back to this list-order tie-break.
//
// `viewer` is the viewer IDENTITY (pickDefaultDaemon's twin; a bare id still
// type-checks and degrades to the old compare). Narrower on the GRANT axis, NOT the
// identity one: `daemons.owner_id` is dual-namespace here too, so a raw compare
// labelled the viewer's own machine "Remote". The no-viewer guard is now isMe's own.
export function pickOwnDaemon<D extends { id: string; ownerId: string }>(
  daemons: readonly D[],
  viewer: ViewerIdentity,
  isOnline: (daemon: D) => boolean,
  selectedId?: string | null,
): string | null {
  if (daemons.length === 0) return null;
  if (selectedId) {
    const selected = daemons.find((d) => d.id === selectedId);
    if (selected && isMe(selected.ownerId, viewer)) return selected.id;
  }
  const ownedOnline = daemons.find((d) => isMe(d.ownerId, viewer) && isOnline(d));
  if (ownedOnline) return ownedOnline.id;
  const owned = daemons.find((d) => isMe(d.ownerId, viewer));
  return owned ? owned.id : null;
}

// The viewer's own daemon, resolved SAFELY where `pickOwnDaemon`'s
// `selectedId` input can't be trusted to exist (see the gap noted on
// `pickOwnDaemon` above). Layers ONE additional, non-inferred signal on top:
// `connectedDaemonId` — the daemon id THIS CLIENT is actually connected to.
// That is literally "the machine I'm at", not an inference over a
// workspace-wide list, so it wins unconditionally when present.
//
// CONFIRMED (round-4 review): in THIS app's shipping topology, the browser/
// Electron renderer never opens a WS to a daemon's own `/ws` — it always
// speaks to the relay's `/api/ws`, and the relay's `cyborg:fetch_daemon_info`
// handler unconditionally returns `{daemonId: null}` (it isn't even in
// DAEMON_FORWARD_TYPES, so it's never forwarded to a real daemon). So
// `connectedDaemonId` is populated from the DESKTOP APP's bridge
// (`desktop_daemon_status`, reading the embedded daemon's own lock file
// directly — no WS involved; wired in app.svelte.ts's connectToServer) and is
// null in a plain browser. `client.fetchDaemonInfo()` is kept ONLY as a
// fallback for a hypothetical direct-daemon client this UI doesn't currently
// have.
//
// When `connectedDaemonId` is null (the plain-browser case — most of this
// product's actual usage), do NOT fail closed: `pickOwnDaemon`'s list-order
// tie-break is DETERMINISTIC now that the underlying query is ordered
// (pg-sync.ts `getDaemonsForWorkspace`), so it names a real, stable daemon —
// occasionally not the exact machine in front of the viewer if they own 2+
// online daemons, but a named, consistent machine tag beats no badge at all
// on the one surface whose entire job is telling machines apart (a remote
// session indistinguishable from a local one reads as "one daemon" —
// session-scope.ts's own doc on why the ChannelSidebar tag exists at all).
//
// `viewer` is the viewer IDENTITY — forwarded verbatim to pickOwnDaemon, so a caller
// still passing a bare id leaves that owner search exactly as broken as before.
export function resolveViewerDaemonId<D extends { id: string; ownerId: string }>(
  daemons: readonly D[],
  viewer: ViewerIdentity,
  isOnline: (daemon: D) => boolean,
  selectedId: string | null | undefined,
  connectedDaemonId: string | null | undefined,
): string | null {
  if (connectedDaemonId) return connectedDaemonId;
  return pickOwnDaemon(daemons, viewer, isOnline, selectedId);
}

// Parses the desktop bridge's `desktop_daemon_status` response
// (CyborgDaemonStatus, packages/desktop-cyborg/daemon-manager.ts) into a
// usable connected-daemon id for `resolveViewerDaemonId` above. Extracted so
// the parsing has a genuine unit-test surface — app.svelte.ts itself can't be
// runtime-imported under this package's vitest (window/document side effects
// + a live client instantiated at module load), so its OWN use of this is
// locked by a source scan instead (see the app.svelte.ts source-scan test).
// `serverId` is `""` (empty string) for a stopped/errored daemon
// (daemon-manager.ts `resolveStatus`) — that must read as "no signal", not
// "the empty daemon" — and the bridge call can reject/return something
// unexpected, so this stays defensive about the input shape.
export function extractConnectedDaemonId(status: unknown): string | null {
  const serverId = (status as { serverId?: unknown } | null | undefined)?.serverId;
  return typeof serverId === "string" && serverId.length > 0 ? serverId : null;
}

// Self-healing retry around the desktop bridge's `desktop_daemon_status` —
// without it, `connectedDaemonId` can silently stay null for the WHOLE app
// session. `main.ts` fires `startDesktopDaemon()` in PARALLEL with
// `createWindow()`, so on a cold relaunch the renderer can reach
// `connectToServer` (and therefore this call) before the embedded daemon has
// even written `paseo.pid` — `resolveStatus()` then returns `serverId: ""`
// (daemon-manager.ts: stopped/starting/errored all read this way), a ONE-SHOT
// caller would never look again, and the Remote/Local badge falls back to
// `pickOwnDaemon`'s list-order guess for the rest of the session — exactly
// the coin-flip this whole fix exists to remove.
//
// No "daemon ready" IPC event exists to listen for instead (checked:
// desktop-cyborg/main.ts only ever sends `cyborg7:event:navigate` /
// `update-status` / `setup-terminal-*` — nothing daemon-lifecycle-related),
// and adding one means touching the Electron main process (a different
// ownership boundary / build+deploy surface than this renderer-only fix), so
// this polls instead: a bounded, injectable-timing retry (fetchStatus + sleep
// as params, no real timers needed) that keeps trying until a non-empty
// serverId comes back or the budget runs out.
export async function pollForConnectedDaemonId(
  fetchStatus: () => Promise<unknown>,
  opts: {
    intervalMs: number;
    maxAttempts: number;
    sleep: (ms: number) => Promise<void>;
  },
): Promise<string | null> {
  for (let attempt = 0; attempt < opts.maxAttempts; attempt++) {
    // A failed probe IS the "daemon not up yet" signal this loop exists to ride
    // out, so the next attempt is the handling. Surfacing it would toast once per
    // second through a normal cold launch. If every attempt fails the loop returns
    // null and the badge falls back to pickOwnDaemon's list order — the documented
    // degraded behaviour.
    // intentional: retry IS the error handling; exhaustion is reported by returning null.
    const status = await fetchStatus().catch(() => null);
    const serverId = extractConnectedDaemonId(status);
    if (serverId) return serverId;
    if (attempt < opts.maxAttempts - 1) await opts.sleep(opts.intervalMs);
  }
  return null;
}
